/* eslint-disable react/require-default-props */
import React from 'react';

import { makeStyles } from 'tss-react/mui';

import { units } from '@hbf/dsl/core';

const useListingCardShadowStyles = makeStyles<{ offsetLevel: number }>()(
  (theme, { offsetLevel }) => ({
    shadow: {
      '--shadow-index': offsetLevel,

      position: 'absolute',
      display: 'inline-flex',
      flexDirection: 'column',
      borderRadius: theme.utils.borderRadius('ref/radius/lg'),
      border: units.border(1, 'solid', theme.palette.mono.main),
      background: theme.palette.mono.light,
      overflow: 'hidden',
      height: 'calc(100% - var(--shadow-offset) * var(--shadow-count))',
      width: 'calc(100% - var(--shadow-offset) * var(--shadow-count))',
      inset: 0,
      transform:
        'translate(calc(var(--shadow-offset) * var(--shadow-index)), calc(var(--shadow-offset) * var(--shadow-index)))',
    },
  }),
);

interface ListingCardShadowProps {
  offsetLevel: number;
  className?: string;
}

const ListingCardShadow = ({
  offsetLevel,
  className,
}: ListingCardShadowProps) => {
  const { classes, cx } = useListingCardShadowStyles({ offsetLevel });

  return <div className={cx(classes.shadow, className)} />;
};

const useListingCardContainerStyles = makeStyles()(() => ({
  container: {
    height: '100%',
    width: '100%',
    maxWidth: '600px',
  },
}));

const ListingCardContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { classes, cx } = useListingCardContainerStyles();

  return <div className={cx(classes.container, className)}>{children}</div>;
};

const useStyles = makeStyles()(() => ({
  container: {
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    height: 'calc(100% - var(--shadow-offset, 0px) * var(--shadow-count, 0))',
    // ensure the card height artificially expands to accommodate the shadow cutout (when content cannot shrink anymore)
    marginBottom: 'calc(var(--shadow-offset, 0px) * var(--shadow-count, 0))',
    width: 'calc(100% - var(--shadow-offset, 0px) * var(--shadow-count, 0))',
  },
}));

export const ListingCardBase = ({
  children,
  className,
  onMouseEnter,
  onMouseLeave,
}: {
  children: React.ReactNode;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  const { classes, cx } = useStyles();

  return (
    <div
      className={cx(classes.container, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-test-locator="ListingCard"
    >
      {children}
    </div>
  );
};

ListingCardBase.Shadow = ListingCardShadow;
ListingCardBase.Container = ListingCardContainer;
